/* eslint-disable max-lines */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { toast } from "sonner"

import type { WorkflowItem } from "@/api/models"
import type { Edge, Node } from "@xyflow/react"

import { AlertTriangle, Loader2, TestTube2 } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { SidebarInset } from "@/components/ui/sidebar"
import { useGetWorkflow } from "@/features/ai-explorer/hooks/use-get-workflow"
import { updateWorkflowChatHistoryApi } from "@/features/ai-explorer/hooks/use-update-workflow-chat-history"
import { useUpsertWorkflow } from "@/features/ai-explorer/hooks/use-upsert-workflow"
import { useProjects } from "@/hooks/use-projects"
import { useViewportBelow } from "@/hooks/use-viewport-below"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"
import { usePendingWorkflowStore } from "@/stores/pending-workflow-store"

import { WorkflowAiChatPanel } from "../components/workflow-ai-chat-panel"
import { WorkflowEditor } from "../components/workflow-editor"
import { WorkflowEditorToolbar } from "../components/workflow-editor-toolbar"
import { WorkflowRunsDialog } from "../components/workflow-runs-dialog"
import { WorkflowTestPanel } from "../components/workflow-test-panel"
import { WorkflowValidationDialog } from "../components/workflow-validation-dialog"
import { useWorkflowAiChatStore } from "../stores/workflow-ai-chat-store"
import { useWorkflowStore } from "../stores/workflow-store"
import { isWorkflowAdminFromSettings } from "../utils/workflow-admin-policy"
import { normalizeWorkflowEdges } from "../utils/workflow-edge-utils"
import { getWorkflowEditorLoadState } from "../utils/workflow-editor-load-state"
import { canEditWorkflow } from "../utils/workflow-permissions"
import {
  serializeWorkflowGraph,
  serializeWorkflowMetadata,
} from "../utils/workflow-persistence-utils"
import { resolveStartFields } from "../utils/workflow-run-utils"
import { getWorkflowSaveValidationAction } from "../utils/workflow-save-validation"
import {
  getRunnableWorkflowId,
  shouldOpenWorkflowTestPanel,
} from "../utils/workflow-test-panel-utils"
import { getWorkflowIssues, type WorkflowIssue } from "../utils/workflow-validation"
import { runWorkflowValidationWarningAction } from "../utils/workflow-validation-dialog-utils"

type WorkflowVisibility = WorkflowItem["visibility"]
type WorkflowSaveOutcome =
  | { status: "saved"; workflow: WorkflowItem }
  | { status: "superseded" | "failed" | "cancelled" }

function getContentRevision(workflow: Pick<WorkflowItem, "content_revision">): number {
  const revision = workflow.content_revision
  return Number.isInteger(revision) && revision !== undefined && revision >= 0 ? revision : 0
}

function parseWorkflowGraphContent(contentString: string): { nodes: Node[]; edges: Edge[] } | null {
  try {
    const parsed = JSON.parse(contentString) as { nodes?: Node[]; edges?: Edge[] }
    if (!Array.isArray(parsed.nodes) || !Array.isArray(parsed.edges)) {
      return null
    }
    return { nodes: parsed.nodes, edges: parsed.edges }
  } catch {
    return null
  }
}

function syncCanvasToSavedGraph(
  contentString: string,
  loadGraph: (nodes: Node[], edges: Edge[]) => void
): string | null {
  const parsed = parseWorkflowGraphContent(contentString)
  if (!parsed) return null
  loadGraph(parsed.nodes, parsed.edges)
  const loadedState = useWorkflowStore.getState()
  return serializeWorkflowGraph(loadedState.nodes, loadedState.edges)
}

type DivergentCanvasSaveReconcile =
  | { kind: "unchanged" }
  | { kind: "synced"; signature: string }
  | { kind: "superseded" }

function reconcileDivergentCanvasAfterSave(args: {
  contentString: string
  canvasSignature: string
  appliedSignature: string | null
  deferredSignature: string | null
  loadGraph: (nodes: Node[], edges: Edge[]) => void
}): DivergentCanvasSaveReconcile {
  const matchesApplied =
    args.appliedSignature !== null && args.appliedSignature === args.canvasSignature
  const matchesDeferred =
    args.deferredSignature !== null && args.deferredSignature === args.canvasSignature
  if (args.canvasSignature === args.contentString || (!matchesApplied && !matchesDeferred)) {
    return { kind: "unchanged" }
  }
  const syncedSignature = syncCanvasToSavedGraph(args.contentString, args.loadGraph)
  if (syncedSignature === null) return { kind: "superseded" }
  return { kind: "synced", signature: syncedSignature }
}

// Stable id so repeated auto-save failures update a single persistent toast
// instead of stacking a new one every 30s.
const AUTO_SAVE_FAILURE_TOAST_ID = "workflow-autosave-failure"
const EMPTY_WORKFLOW_METADATA = serializeWorkflowMetadata({
  title: "My Workflow",
  visibility: "personal",
  defaultProjectId: undefined,
})

export function WorkflowEditorPage() {
  const { t } = useTranslation(undefined, { keyPrefix: "workflowLanding" })
  const { t: tGlobal } = useTranslation()
  const describeIssue = useCallback(
    (issue: WorkflowIssue | null | undefined) =>
      issue
        ? tGlobal(issue.messageKey, { defaultValue: issue.message, ...issue.messageParams })
        : undefined,
    [tGlobal]
  )
  const { nodes, edges, loadGraph, resetGraph, clearExecution, requestAutoLayout } =
    useWorkflowStore()
  const isExecuting = useWorkflowStore((state) => state.isExecuting)
  const setActiveWorkflowChat = useWorkflowAiChatStore((s) => s.setActiveWorkflow)
  const clearWorkflowChat = useWorkflowAiChatStore((s) => s.clear)
  const isAiChatOpen = useWorkflowAiChatStore((s) => s.isOpen)
  const setIsAiChatOpen = useWorkflowAiChatStore((s) => s.setIsOpen)
  const [allowNarrowAiChatOpen, setAllowNarrowAiChatOpen] = useState(false)
  const upsertWorkflowMutation = useUpsertWorkflow()
  const [workflowTitle, setWorkflowTitle] = useState("My Workflow")
  const [workflowVisibility, setWorkflowVisibility] = useState<WorkflowVisibility>("personal")
  const [defaultProjectId, setDefaultProjectId] = useState<string | undefined>(undefined)
  const [showLegacyDialog, setShowLegacyDialog] = useState(false)
  const [showTestPanel, setShowTestPanel] = useState(false)
  const [showRunsDialog, setShowRunsDialog] = useState(false)
  const [showValidationWarning, setShowValidationWarning] = useState(false)
  const [validationWarningAction, setValidationWarningAction] = useState<
    "save" | "run-in-explorer"
  >("save")
  const [savedWorkflowId, setSavedWorkflowId] = useState<string | undefined>(undefined)
  const [isAutoSaving, setIsAutoSaving] = useState(false)
  const lastSavedContentRef = useRef<string | null>(null)
  const lastSavedMetadataRef = useRef<string | null>(null)
  const serializedContent = useMemo(() => serializeWorkflowGraph(nodes, edges), [nodes, edges])
  const initialContentRef = useRef(serializedContent)
  const currentContentRef = useRef<string>("")
  currentContentRef.current = serializedContent
  const currentMetadataRef = useRef<string>("")
  currentMetadataRef.current = serializeWorkflowMetadata({
    title: workflowTitle,
    visibility: workflowVisibility,
    defaultProjectId,
  })
  // Consecutive auto-save failures. Surfaced via a persistent toast (kept alive
  // with a stable id) so a failing auto-save — expired auth, a 5xx, a backend
  // validation reject — is never silent; reset to 0 the moment a save succeeds.
  const autoSaveFailureCountRef = useRef(0)
  const chatInitializedForRef = useRef<string | null>(null)
  const graphInitializedForRef = useRef<string | null>(null)
  const appliedServerContentRef = useRef<{
    workflowId: string
    revision: number
    signature: string
  } | null>(null)
  const deferredServerGraphRef = useRef<{
    workflowId: string
    revision: number
    signature: string
    nodes: Node[]
    edges: Edge[]
  } | null>(null)
  const latestObservedContentRevisionRef = useRef<{
    workflowId: string
    revision: number
  } | null>(null)
  // Tracks the previous route id so the reset effect can tell an actual
  // workflow switch (id A -> id B) apart from the initial mount (undefined ->
  // id). Only a real switch should tear down test-run state; on mount there is
  // nothing to tear down, and doing so anyway races the ?test=true auto-open
  // effect (both fire in the same commit, and this effect running later would
  // clobber the panel back closed).
  const previousWorkflowIdRef = useRef<string | undefined>(undefined)
  const { id } = useParams<{ id: string }>()
  const routeOwnerRef = useRef({ id, generation: 0 })
  if (routeOwnerRef.current.id !== id) {
    routeOwnerRef.current = {
      id,
      generation: routeOwnerRef.current.generation + 1,
    }
  }
  const saveTailsRef = useRef(new Map<string, Promise<void>>())
  const savedWorkflowIdRef = useRef<string | undefined>(savedWorkflowId)
  savedWorkflowIdRef.current = savedWorkflowId
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const setPendingWorkflow = usePendingWorkflowStore((state) => state.setPendingWorkflow)
  const runnableWorkflowId = getRunnableWorkflowId(id, savedWorkflowId)

  // Below the lg breakpoint the side panels render as overlay drawers (they'd
  // otherwise cover the canvas). Auto-close the AI Builder when the viewport
  // crosses into narrow so the canvas is visible by default; the user can still
  // re-open it as a drawer. Runs only on the narrow transition, not every render.
  const isNarrowViewport = useViewportBelow({ breakpoint: 1024 })
  useLayoutEffect(() => {
    if (isNarrowViewport) {
      setAllowNarrowAiChatOpen(false)
      setIsAiChatOpen(false)
    }
  }, [isNarrowViewport, setIsAiChatOpen])
  const effectiveAiChatOpen = isAiChatOpen && (!isNarrowViewport || allowNarrowAiChatOpen)

  const handleToggleAiChat = useCallback(() => {
    if (isNarrowViewport) {
      const nextOpen = !effectiveAiChatOpen
      setAllowNarrowAiChatOpen(nextOpen)
      setIsAiChatOpen(nextOpen)
      return
    }
    setIsAiChatOpen(!isAiChatOpen)
  }, [effectiveAiChatOpen, isAiChatOpen, isNarrowViewport, setIsAiChatOpen])

  // Auto-open test panel if coming from landing page with test=true param
  useEffect(() => {
    if (shouldOpenWorkflowTestPanel(searchParams, id, savedWorkflowId)) {
      setShowTestPanel(true)
      const nextParams = new URLSearchParams(searchParams)
      nextParams.delete("test")
      setSearchParams(nextParams, { replace: true })
    }
  }, [id, searchParams, setSearchParams, savedWorkflowId])

  // Auto-open the Runs dialog when arriving from an approval/escalation email deep
  // link (/flows/:id?run=<id>) so the approver lands on the list where pending
  // approvals can be acted on. Clear the param so a refresh doesn't re-open it.
  useEffect(() => {
    if (searchParams.get("run")) {
      setShowRunsDialog(true)
      const nextParams = new URLSearchParams(searchParams)
      nextParams.delete("run")
      setSearchParams(nextParams, { replace: true })
    }
  }, [searchParams, setSearchParams])

  // Expose the open workflow's id to the store so workflow-scoped node actions
  // (e.g. the attachment node's upload) can read it.
  const setWorkflowIdInStore = useWorkflowStore((state) => state.setWorkflowId)
  useEffect(() => {
    setWorkflowIdInStore(id || savedWorkflowId || null)
  }, [id, savedWorkflowId, setWorkflowIdInStore])

  // Check if user is workflow admin. `settings` is `{} as Config` until
  // /auth/config resolves, so user_policy can be undefined during the startup
  // race — read it null-safely (default false) to avoid crashing the editor.
  const settingsService = SettingsService.getInstance()
  const isWorkflowAdmin = isWorkflowAdminFromSettings(settingsService.settings)

  // Fetch projects for project selector
  const { data: projects = [] } = useProjects()

  // Only fetch if we have an ID
  const { data: workflowData, isError, isLoading } = useGetWorkflow(id || "")
  const loadState = getWorkflowEditorLoadState({
    hasRouteWorkflowId: Boolean(id),
    hasWorkflowData: Boolean(workflowData),
    isError,
    isLoading,
  })

  const canEdit = !id || canEditWorkflow({ visibility: workflowVisibility }, isWorkflowAdmin)

  // Reset graph when ID changes (switching workflows or creating new)
  useEffect(() => {
    const previousWorkflowId = chatInitializedForRef.current
    if (previousWorkflowId && previousWorkflowId !== id) {
      const currentMessages = useWorkflowAiChatStore.getState().messages
      if (currentMessages.length > 0) {
        void updateWorkflowChatHistoryApi(previousWorkflowId, { chat_history: currentMessages })
      }
    }
    resetGraph()
    const resetState = useWorkflowStore.getState()
    initialContentRef.current = serializeWorkflowGraph(resetState.nodes, resetState.edges)
    // Switching workflows must not leave the previous flow's test run bleeding
    // into the newly opened one: close the test panel and clear execution state
    // (isExecuting, executingNodeIds) so the new canvas isn't stuck read-only
    // with a stale "Test Run in Progress" banner or spinners from id collisions.
    // Only do this on an actual switch between two distinct ids — skip the
    // initial mount, otherwise this races (and clobbers) the ?test=true
    // auto-open effect above, which also sets showTestPanel in the same commit.
    const isWorkflowSwitch =
      previousWorkflowIdRef.current !== undefined && previousWorkflowIdRef.current !== id
    if (isWorkflowSwitch) {
      setShowTestPanel(false)
      clearExecution()
    }
    previousWorkflowIdRef.current = id
    setWorkflowTitle("My Workflow")
    setWorkflowVisibility("personal")
    setDefaultProjectId(undefined)
    setShowLegacyDialog(false)
    setSavedWorkflowId(id)
    savedWorkflowIdRef.current = id
    chatInitializedForRef.current = null
    graphInitializedForRef.current = null
    appliedServerContentRef.current = null
    deferredServerGraphRef.current = null
    latestObservedContentRevisionRef.current = null
    lastSavedContentRef.current = null
    lastSavedMetadataRef.current = null
    clearWorkflowChat()
  }, [id, resetGraph, clearExecution, clearWorkflowChat])

  // Load workflow data when it arrives
  useEffect(() => {
    if (!workflowData) return
    const incomingRevision = getContentRevision(workflowData)
    const latestObservedRevision =
      latestObservedContentRevisionRef.current?.workflowId === workflowData.id
        ? latestObservedContentRevisionRef.current.revision
        : -1
    if (incomingRevision < latestObservedRevision) return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let content: any
    let isValidContent = false

    try {
      content =
        typeof workflowData.content === "string"
          ? JSON.parse(workflowData.content)
          : workflowData.content

      if (content?.nodes && content?.edges) {
        isValidContent = true
      }
    } catch {
      // ignore
    }

    if (isValidContent) {
      const alreadyInitialized = graphInitializedForRef.current === workflowData.id
      const storeState = useWorkflowStore.getState()
      const currentContent = serializeWorkflowGraph(storeState.nodes, storeState.edges)
      const hasLocalChanges =
        lastSavedContentRef.current !== null && lastSavedContentRef.current !== currentContent
      const normalizedServerEdges = normalizeWorkflowEdges(content.edges)
      const serverContentSignature = serializeWorkflowGraph(content.nodes, normalizedServerEdges)
      const appliedServerContent = appliedServerContentRef.current
      const deferredServerGraph = deferredServerGraphRef.current
      let orderingBaseline =
        appliedServerContent?.workflowId === workflowData.id ? appliedServerContent : null
      if (
        deferredServerGraph?.workflowId === workflowData.id &&
        (!orderingBaseline || deferredServerGraph.revision >= orderingBaseline.revision)
      ) {
        orderingBaseline = deferredServerGraph
      }

      if (
        orderingBaseline &&
        (incomingRevision < orderingBaseline.revision ||
          (incomingRevision === orderingBaseline.revision &&
            serverContentSignature !== orderingBaseline.signature))
      ) {
        return
      }
      latestObservedContentRevisionRef.current = {
        workflowId: workflowData.id,
        revision: incomingRevision,
      }
      const fetchedMetadata = serializeWorkflowMetadata({
        title: workflowData.title,
        visibility: workflowData.visibility as WorkflowVisibility,
        defaultProjectId: workflowData.default_project_id,
      })
      const previousSavedMetadata = lastSavedMetadataRef.current
      if (previousSavedMetadata === null || currentMetadataRef.current === previousSavedMetadata) {
        setWorkflowTitle(workflowData.title)
        setWorkflowVisibility(workflowData.visibility as WorkflowVisibility)
        setDefaultProjectId(workflowData.default_project_id)
        lastSavedMetadataRef.current = fetchedMetadata
      }

      const serverContentChanged =
        appliedServerContent?.workflowId === workflowData.id &&
        appliedServerContent.signature !== serverContentSignature
      const serverContentNeedsApply = !alreadyInitialized || serverContentChanged

      if (serverContentNeedsApply && !hasLocalChanges) {
        deferredServerGraphRef.current = null
        loadGraph(content.nodes, content.edges)
        const loadedState = useWorkflowStore.getState()
        lastSavedContentRef.current = serializeWorkflowGraph(loadedState.nodes, loadedState.edges)
        graphInitializedForRef.current = workflowData.id
        setShowLegacyDialog(false)
        appliedServerContentRef.current = {
          workflowId: workflowData.id,
          revision: incomingRevision,
          signature: lastSavedContentRef.current,
        }

        const shouldAutoLayout =
          searchParams.get("autolayout") === "true" || searchParams.get("autolayout") === "1"
        if (shouldAutoLayout) {
          requestAutoLayout()
          const nextParams = new URLSearchParams(searchParams)
          nextParams.delete("autolayout")
          setSearchParams(nextParams, { replace: true })
        }
      } else if (serverContentNeedsApply && hasLocalChanges) {
        deferredServerGraphRef.current = {
          workflowId: workflowData.id,
          revision: incomingRevision,
          signature: serverContentSignature,
          nodes: content.nodes,
          edges: normalizedServerEdges,
        }
      } else if (!serverContentChanged) {
        deferredServerGraphRef.current = null
        setShowLegacyDialog(false)
        appliedServerContentRef.current = {
          workflowId: workflowData.id,
          revision: incomingRevision,
          signature: serverContentSignature,
        }
      }
    } else {
      const appliedServerContent = appliedServerContentRef.current
      const deferredServerGraph = deferredServerGraphRef.current
      const latestKnownRevision = Math.max(
        appliedServerContent?.workflowId === workflowData.id ? appliedServerContent.revision : -1,
        deferredServerGraph?.workflowId === workflowData.id ? deferredServerGraph.revision : -1,
        latestObservedRevision
      )
      if (incomingRevision <= latestKnownRevision) return
      latestObservedContentRevisionRef.current = {
        workflowId: workflowData.id,
        revision: incomingRevision,
      }
      if (
        deferredServerGraph?.workflowId === workflowData.id &&
        deferredServerGraph.revision < incomingRevision
      ) {
        deferredServerGraphRef.current = null
      }

      const fetchedMetadata = serializeWorkflowMetadata({
        title: workflowData.title,
        visibility: workflowData.visibility as WorkflowVisibility,
        defaultProjectId: workflowData.default_project_id,
      })
      const previousSavedMetadata = lastSavedMetadataRef.current
      if (previousSavedMetadata === null || currentMetadataRef.current === previousSavedMetadata) {
        setWorkflowTitle(workflowData.title)
        setWorkflowVisibility(workflowData.visibility as WorkflowVisibility)
        setDefaultProjectId(workflowData.default_project_id)
        lastSavedMetadataRef.current = fetchedMetadata
      }

      if (graphInitializedForRef.current !== workflowData.id) {
        setShowLegacyDialog(true)
      }
    }

    // Only initialize chat history once per workflow (avoid re-setting on refetch)
    if (chatInitializedForRef.current !== workflowData.id) {
      chatInitializedForRef.current = workflowData.id
      setActiveWorkflowChat(
        workflowData.id,
        Array.isArray(workflowData.chat_history) ? workflowData.chat_history : []
      )
    }
  }, [
    workflowData,
    loadGraph,
    requestAutoLayout,
    searchParams,
    setSearchParams,
    setActiveWorkflowChat,
  ])

  useEffect(() => {
    const deferredServerGraph = deferredServerGraphRef.current
    if (!deferredServerGraph || deferredServerGraph.workflowId !== id) return
    if (lastSavedContentRef.current === null || serializedContent !== lastSavedContentRef.current) {
      return
    }
    if (
      appliedServerContentRef.current?.workflowId === deferredServerGraph.workflowId &&
      appliedServerContentRef.current.revision === deferredServerGraph.revision &&
      appliedServerContentRef.current.signature === deferredServerGraph.signature
    ) {
      deferredServerGraphRef.current = null
      return
    }

    deferredServerGraphRef.current = null
    loadGraph(deferredServerGraph.nodes, deferredServerGraph.edges)
    lastSavedContentRef.current = deferredServerGraph.signature
    graphInitializedForRef.current = deferredServerGraph.workflowId
    appliedServerContentRef.current = {
      workflowId: deferredServerGraph.workflowId,
      revision: deferredServerGraph.revision,
      signature: deferredServerGraph.signature,
    }
  }, [id, loadGraph, serializedContent])

  const workflowIssues = useMemo(() => getWorkflowIssues(nodes, edges), [nodes, edges])
  const blockingIssue = workflowIssues.find((issue) => issue.severity === "error") ?? null
  const hasMissingProcessorWarning = workflowIssues.some(
    (issue) => issue.code === "missing_processor"
  )

  // Perform the actual save operation
  const performSave = useCallback(
    async (showToast = true): Promise<WorkflowSaveOutcome> => {
      const ownerGeneration = routeOwnerRef.current.generation
      const ownerRouteId = id
      const persistenceId = ownerRouteId || savedWorkflowIdRef.current
      const queueKey = persistenceId ? `workflow:${persistenceId}` : `new-route:${ownerGeneration}`
      const queueKeys = new Set([queueKey])
      const contentString = serializedContent
      const metadataString = serializeWorkflowMetadata({
        title: workflowTitle,
        visibility: workflowVisibility,
        defaultProjectId,
      })
      const save = async (): Promise<WorkflowSaveOutcome> => {
        if (routeOwnerRef.current.generation !== ownerGeneration) return { status: "cancelled" }

        try {
          if (workflowVisibility === "shared") {
            if (showToast) {
              toast.error(t("editor.readOnlyShared", "Shared workflows are read-only"))
            }
            return { status: "failed" }
          }
          const targetWorkflowId = persistenceId || savedWorkflowIdRef.current
          const isNewWorkflow = !targetWorkflowId
          const result = await upsertWorkflowMutation.mutateAsync({
            id: targetWorkflowId,
            title: workflowTitle,
            content: contentString,
            file_required: false,
            visibility: workflowVisibility,
            default_project_id: defaultProjectId,
          })
          if (routeOwnerRef.current.generation !== ownerGeneration) return { status: "cancelled" }

          if (result.id) {
            if (isNewWorkflow) {
              const persistedQueueKey = `workflow:${result.id}`
              const promotedTail = saveTailsRef.current.get(queueKey) ?? nextTail
              saveTailsRef.current.set(persistedQueueKey, promotedTail)
              if (promotedTail === nextTail) {
                queueKeys.add(persistedQueueKey)
              } else {
                void promotedTail.then(() => {
                  if (saveTailsRef.current.get(persistedQueueKey) === promotedTail) {
                    saveTailsRef.current.delete(persistedQueueKey)
                  }
                })
              }
            }
            savedWorkflowIdRef.current = result.id
            setSavedWorkflowId(result.id)
            if (isNewWorkflow) {
              chatInitializedForRef.current = result.id
              setActiveWorkflowChat(result.id, useWorkflowAiChatStore.getState().messages)
            }
          }
          const savedWorkflowIdentity = result.id || targetWorkflowId
          if (savedWorkflowIdentity) {
            const appliedServerContent =
              appliedServerContentRef.current?.workflowId === savedWorkflowIdentity
                ? appliedServerContentRef.current
                : null
            const deferredServerGraph =
              deferredServerGraphRef.current?.workflowId === savedWorkflowIdentity
                ? deferredServerGraphRef.current
                : null
            const observedRevision =
              latestObservedContentRevisionRef.current?.workflowId === savedWorkflowIdentity
                ? latestObservedContentRevisionRef.current.revision
                : -1
            const hasExplicitRevision = result.content_revision !== undefined
            const resultRevision = hasExplicitRevision
              ? getContentRevision(result)
              : (appliedServerContent?.revision ?? 0)
            let orderingBaseline = appliedServerContent
            if (
              deferredServerGraph &&
              (!orderingBaseline || deferredServerGraph.revision >= orderingBaseline.revision)
            ) {
              orderingBaseline = deferredServerGraph
            }
            const resultConflictsWithObservedRevision =
              hasExplicitRevision && resultRevision < observedRevision
            const resultConflictsWithContent =
              hasExplicitRevision &&
              orderingBaseline !== null &&
              (resultRevision < orderingBaseline.revision ||
                (resultRevision === orderingBaseline.revision &&
                  contentString !== orderingBaseline.signature))

            if (resultConflictsWithObservedRevision || resultConflictsWithContent) {
              const deferredWins =
                deferredServerGraph &&
                (deferredServerGraph.revision > resultRevision ||
                  (deferredServerGraph.revision === resultRevision &&
                    deferredServerGraph.signature !== contentString))
              if (deferredWins && currentContentRef.current === contentString) {
                deferredServerGraphRef.current = null
                loadGraph(deferredServerGraph.nodes, deferredServerGraph.edges)
                lastSavedContentRef.current = deferredServerGraph.signature
                graphInitializedForRef.current = deferredServerGraph.workflowId
                appliedServerContentRef.current = {
                  workflowId: deferredServerGraph.workflowId,
                  revision: deferredServerGraph.revision,
                  signature: deferredServerGraph.signature,
                }
              }
              return { status: "superseded" }
            }

            // Canvas already shows applied/deferred server content that differs from the
            // in-flight save payload (e.g. deferred apply after undo while save was pending).
            // Sync the canvas to the successful write so baselines and Test/Explorer handoffs
            // cannot keep a clean-but-divergent deferred graph against the saved server content.
            const divergentCanvas = reconcileDivergentCanvasAfterSave({
              contentString,
              canvasSignature: currentContentRef.current,
              appliedSignature: appliedServerContent?.signature ?? null,
              deferredSignature: deferredServerGraph?.signature ?? null,
              loadGraph,
            })
            if (divergentCanvas.kind === "superseded") {
              latestObservedContentRevisionRef.current = hasExplicitRevision
                ? {
                    workflowId: savedWorkflowIdentity,
                    revision: Math.max(observedRevision, resultRevision),
                  }
                : latestObservedContentRevisionRef.current
              return { status: "superseded" }
            }
            const acceptedSignature =
              divergentCanvas.kind === "synced" ? divergentCanvas.signature : contentString
            if (divergentCanvas.kind === "synced") {
              currentContentRef.current = acceptedSignature
              graphInitializedForRef.current = savedWorkflowIdentity
            }

            lastSavedContentRef.current = acceptedSignature
            lastSavedMetadataRef.current = metadataString
            deferredServerGraphRef.current = null
            appliedServerContentRef.current = {
              workflowId: savedWorkflowIdentity,
              revision: resultRevision,
              signature: acceptedSignature,
            }
            latestObservedContentRevisionRef.current = {
              workflowId: savedWorkflowIdentity,
              revision: Math.max(observedRevision, resultRevision),
            }
          } else {
            lastSavedContentRef.current = contentString
            lastSavedMetadataRef.current = metadataString
          }
          if (autoSaveFailureCountRef.current > 0) {
            autoSaveFailureCountRef.current = 0
            toast.dismiss(AUTO_SAVE_FAILURE_TOAST_ID)
          }
          if (showToast) {
            toast.success(t("editor.workflowSaved"))
          }
          return { status: "saved", workflow: result }
        } catch {
          if (routeOwnerRef.current.generation !== ownerGeneration) return { status: "cancelled" }
          // Persistent (non-auto-dismissing) toast so a save that keeps failing —
          // expired auth, a 5xx, a backend validation reject — is never silent,
          // even when this call came from the 30s auto-save (showToast=false).
          // Keyed by a stable id so repeated failures update it in place instead
          // of stacking a new toast every interval tick.
          autoSaveFailureCountRef.current += 1
          toast.error(
            t(
              "editor.workflowAutoSaveFailed",
              "Auto-save is failing — your latest changes are not saved"
            ),
            { id: AUTO_SAVE_FAILURE_TOAST_ID, duration: Infinity }
          )
          if (showToast) {
            toast.error(t("editor.workflowSaveError"))
          }
          return { status: "failed" }
        }
      }

      const previousTail = saveTailsRef.current.get(queueKey) ?? Promise.resolve()
      const resultPromise = previousTail.then(save, save)
      const nextTail = resultPromise.then(
        () => undefined,
        () => undefined
      )
      saveTailsRef.current.set(queueKey, nextTail)
      void nextTail.then(() => {
        for (const ownedKey of queueKeys) {
          if (saveTailsRef.current.get(ownedKey) === nextTail) {
            saveTailsRef.current.delete(ownedKey)
          }
        }
      })
      return resultPromise
    },
    [
      serializedContent,
      id,
      workflowTitle,
      workflowVisibility,
      defaultProjectId,
      upsertWorkflowMutation,
      setActiveWorkflowChat,
      loadGraph,
      t,
    ]
  )

  // Auto-save every 30 seconds if there are unsaved changes
  useEffect(() => {
    if (!canEdit) return

    const autoSaveInterval = setInterval(async () => {
      const workflowId = id || savedWorkflowId
      if (!workflowId) return

      if (blockingIssue) return

      const currentContent = serializedContent
      const currentMetadata = serializeWorkflowMetadata({
        title: workflowTitle,
        visibility: workflowVisibility,
        defaultProjectId,
      })
      const contentIsDirty = lastSavedContentRef.current !== currentContent
      const metadataIsDirty = lastSavedMetadataRef.current !== currentMetadata
      if (!contentIsDirty && !metadataIsDirty) return

      setIsAutoSaving(true)
      const minDisplayTime = new Promise((resolve) => setTimeout(resolve, 1000))
      await Promise.all([performSave(false), minDisplayTime])
      setIsAutoSaving(false)
    }, 30000)

    return () => clearInterval(autoSaveInterval)
  }, [
    canEdit,
    id,
    savedWorkflowId,
    serializedContent,
    performSave,
    blockingIssue,
    workflowTitle,
    workflowVisibility,
    defaultProjectId,
  ])

  // Warn before the tab is closed/refreshed while edits haven't made it to the
  // backend — either genuinely unsaved local changes, or a run of auto-save
  // failures (expired auth, 5xx, validation reject) that the 30s interval has
  // been silently retrying. Without this, a persistently failing auto-save can
  // lose an entire editing session with zero notice.
  useEffect(() => {
    if (!canEdit) return

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const currentContent = serializedContent
      const currentMetadata = serializeWorkflowMetadata({
        title: workflowTitle,
        visibility: workflowVisibility,
        defaultProjectId,
      })
      const hasUnsavedContent =
        lastSavedContentRef.current === null
          ? initialContentRef.current !== currentContent
          : lastSavedContentRef.current !== currentContent
      const hasUnsavedMetadata =
        lastSavedMetadataRef.current === null
          ? !id && currentMetadata !== EMPTY_WORKFLOW_METADATA
          : lastSavedMetadataRef.current !== currentMetadata
      const hasUnsavedChanges = hasUnsavedContent || hasUnsavedMetadata
      const hasFailingAutoSave = autoSaveFailureCountRef.current > 0

      if (hasUnsavedChanges || hasFailingAutoSave) {
        event.preventDefault()
        // Legacy browsers require returnValue to be set to trigger the prompt.
        event.returnValue = ""
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => window.removeEventListener("beforeunload", handleBeforeUnload)
  }, [canEdit, id, serializedContent, workflowTitle, workflowVisibility, defaultProjectId])

  const handleSave = useCallback(async () => {
    const action = getWorkflowSaveValidationAction({ blockingIssue, hasMissingProcessorWarning })
    if (action === "block") {
      toast.error(t("editor.invalidWorkflow"), { description: describeIssue(blockingIssue) })
      return
    }
    if (action === "warn") {
      setValidationWarningAction("save")
      setShowValidationWarning(true)
      return
    }
    await performSave()
  }, [blockingIssue, hasMissingProcessorWarning, performSave, t])

  const runInExplorer = useCallback(async () => {
    let workflowToRun: WorkflowItem | null = workflowData ?? null
    if (canEdit) {
      const ownerGeneration = routeOwnerRef.current.generation
      const contentToRun = serializedContent
      const metadataToRun = currentMetadataRef.current
      const saveOutcome = await performSave(false)
      if (saveOutcome.status !== "saved") {
        if (routeOwnerRef.current.generation !== ownerGeneration) return
        if (saveOutcome.status === "failed") {
          toast.error(t("editor.workflowSaveError"))
        }
        return
      }
      workflowToRun = saveOutcome.workflow
      if (
        routeOwnerRef.current.generation !== ownerGeneration ||
        currentContentRef.current !== contentToRun ||
        currentMetadataRef.current !== metadataToRun
      ) {
        return
      }
    } else if (!workflowToRun) {
      toast.error(t("editor.workflowSaveError"))
      return
    }

    setPendingWorkflow({
      ...workflowToRun,
      title: workflowToRun.title || workflowTitle,
      content: workflowToRun.content || JSON.stringify({ nodes, edges }),
      file_required: workflowToRun.file_required,
      default_project_id: workflowToRun.default_project_id ?? defaultProjectId,
    })

    void navigate("/explorer")
  }, [
    workflowData,
    canEdit,
    performSave,
    t,
    setPendingWorkflow,
    workflowTitle,
    nodes,
    edges,
    serializedContent,
    defaultProjectId,
    navigate,
  ])

  // Handle save/run after user confirms the validation warning
  const handleConfirmValidationWarning = useCallback(async () => {
    await runWorkflowValidationWarningAction(setShowValidationWarning, async () => {
      if (validationWarningAction === "run-in-explorer") {
        await runInExplorer()
        return
      }
      await performSave()
    })
  }, [performSave, runInExplorer, validationWarningAction])

  const handleRun = useCallback(async () => {
    // Validate before showing test panel
    if (blockingIssue) {
      toast.error(t("editor.invalidWorkflow"), { description: describeIssue(blockingIssue) })
      return
    }

    // Auto-save if there are unsaved changes before running — the test run only
    // sends workflow_id to the backend (see use-workflow-test-run.ts), so it
    // executes whatever content was last saved. A brand-new (unsaved) flow always
    // needs a save; an existing flow needs one whenever the canvas has diverged
    // from lastSavedContentRef, otherwise the run would silently use stale content.
    const workflowId = id || savedWorkflowId
    const currentContent = serializedContent
    const currentMetadata = serializeWorkflowMetadata({
      title: workflowTitle,
      visibility: workflowVisibility,
      defaultProjectId,
    })
    const isDirty =
      !workflowId ||
      lastSavedContentRef.current !== currentContent ||
      (lastSavedMetadataRef.current !== null && lastSavedMetadataRef.current !== currentMetadata)
    if (isDirty) {
      const ownerGeneration = routeOwnerRef.current.generation
      const contentToRun = currentContent
      const metadataToRun = currentMetadata
      const saveOutcome = await performSave(false)
      if (saveOutcome.status !== "saved") {
        if (routeOwnerRef.current.generation !== ownerGeneration) return
        if (saveOutcome.status === "failed") {
          toast.error(t("editor.workflowSaveError"))
        }
        return
      }
      if (
        routeOwnerRef.current.generation !== ownerGeneration ||
        currentContentRef.current !== contentToRun ||
        currentMetadataRef.current !== metadataToRun
      ) {
        return
      }
    }

    // Close AI chat if open to make room for test panel
    if (effectiveAiChatOpen) {
      setAllowNarrowAiChatOpen(false)
      setIsAiChatOpen(false)
    }

    setShowTestPanel(true)
  }, [
    blockingIssue,
    t,
    id,
    savedWorkflowId,
    serializedContent,
    workflowTitle,
    workflowVisibility,
    defaultProjectId,
    performSave,
    effectiveAiChatOpen,
    setIsAiChatOpen,
  ])

  const handleRunInExplorer = useCallback(async () => {
    const action = getWorkflowSaveValidationAction({ blockingIssue, hasMissingProcessorWarning })
    if (action === "block") {
      toast.error(t("editor.invalidWorkflow"), { description: describeIssue(blockingIssue) })
      return
    }
    if (action === "warn") {
      setValidationWarningAction("run-in-explorer")
      setShowValidationWarning(true)
      return
    }
    await runInExplorer()
  }, [blockingIssue, hasMissingProcessorWarning, runInExplorer, t])

  const handleCloseTestPanel = useCallback(() => {
    setShowTestPanel(false)
    clearExecution()
  }, [clearExecution])

  // Extract start node settings for the test panel's legacy single-input path.
  // Derive from resolveStartFields so a single-field fields[] flow renders the
  // same legacy textarea/file picker as a legacy requiresInput/fileMode flow.
  // (Multi-field flows render their own dynamic form inside the test panel.)
  const startFields = useMemo(() => {
    const startNode = nodes.find((n) => n.type === "start")
    return resolveStartFields(startNode?.data as Parameters<typeof resolveStartFields>[0])
  }, [nodes])

  const startNodeAllowsTextInput = useMemo(
    () => startFields.some((field) => field.type === "text"),
    [startFields]
  )

  const startNodeFileMode = useMemo<"none" | "optional" | "required">(() => {
    const fileField = startFields.find((field) => field.type === "file")
    if (!fileField) return "none"
    return fileField.required ? "required" : "optional"
  }, [startFields])

  const handleAiSetMetadata = useCallback(
    ({ title, defaultProjectId }: { title?: string; defaultProjectId?: string | null }) => {
      if (typeof title === "string") {
        setWorkflowTitle(title)
      }

      if (defaultProjectId !== undefined) {
        setDefaultProjectId(defaultProjectId ?? undefined)
      }
    },
    [setDefaultProjectId, setWorkflowTitle]
  )

  if (loadState === "loading") {
    return (
      <SidebarInset className="flex h-[calc(100svh-64px)] min-h-[calc(100svh-64px)] w-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </SidebarInset>
    )
  }

  if (loadState === "error") {
    return (
      <SidebarInset className="flex h-[calc(100svh-64px)] min-h-[calc(100svh-64px)] w-full items-center justify-center p-6">
        <div className="border-border bg-card max-w-md rounded-md border p-6 text-center shadow-sm">
          <AlertTriangle className="text-muted-foreground mx-auto mb-3 size-8" />
          <h2 className="text-lg font-semibold">
            {t("editor.workflowLoadErrorTitle", "Workflow not found")}
          </h2>
          <p className="text-muted-foreground mt-2 text-sm">
            {t(
              "editor.workflowLoadErrorDescription",
              "This workflow could not be loaded. It may have been deleted or you may not have access."
            )}
          </p>
        </div>
      </SidebarInset>
    )
  }

  return (
    <SidebarInset className="flex h-[calc(100svh-64px)] min-h-[calc(100svh-64px)] w-full flex-col overflow-hidden">
      {/* Test Run Banner */}
      {isExecuting && (
        <div className="workflow-test-mode-banner text-warning-ink flex h-8 shrink-0 items-center justify-center gap-2 text-sm font-medium">
          <TestTube2 className="size-4 animate-pulse" />
          {t("editor.testRunInProgress", "Test Run in Progress")}
        </div>
      )}
      <WorkflowEditorToolbar
        canEdit={canEdit}
        defaultProjectId={defaultProjectId}
        isAiChatOpen={effectiveAiChatOpen}
        isAutoSaving={isAutoSaving}
        isSaving={upsertWorkflowMutation.isPending}
        isWorkflowAdmin={isWorkflowAdmin}
        projects={projects}
        showTestPanel={showTestPanel}
        workflowId={id ?? savedWorkflowId ?? undefined}
        workflowTitle={workflowTitle}
        workflowVisibility={workflowVisibility}
        onOpenRunHistory={() => setShowRunsDialog(true)}
        onProjectChange={setDefaultProjectId}
        onRunInExplorer={handleRunInExplorer}
        onSave={handleSave}
        onTestRun={handleRun}
        onTitleChange={setWorkflowTitle}
        onToggleAiChat={handleToggleAiChat}
        onVisibilityChange={setWorkflowVisibility}
      />
      <div className="min-h-0 flex-1 overflow-hidden">
        {/* `relative` anchors the narrow-screen overlay drawers (AI/test panels,
            which are `max-lg:absolute`) to this canvas row — below the toolbar —
            rather than to the page container, so the toolbar stays accessible. */}
        <div className="relative flex h-full min-h-0">
          <div
            className={cn(
              "min-w-0 flex-1 overflow-hidden transition-opacity duration-300",
              isExecuting && "opacity-80"
            )}
          >
            <WorkflowEditor readOnly={!canEdit || isExecuting} />
          </div>
          {canEdit && !showTestPanel && effectiveAiChatOpen && (
            <WorkflowAiChatPanel workflowId={savedWorkflowId} onSetMetadata={handleAiSetMetadata} />
          )}
          <WorkflowTestPanel
            allowTextInput={startNodeAllowsTextInput}
            fileMode={startNodeFileMode}
            isOpen={showTestPanel}
            workflowId={runnableWorkflowId}
            onClose={handleCloseTestPanel}
          />
        </div>
      </div>

      <AlertDialog open={showLegacyDialog} onOpenChange={setShowLegacyDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-warning-ink size-5" />
              {t("editor.legacyWorkflow.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("editor.legacyWorkflow.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>{t("editor.legacyWorkflow.confirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showValidationWarning && (
        <WorkflowValidationDialog
          description={t("editor.validationWarning.description")}
          open={showValidationWarning}
          title={t("editor.validationWarning.title")}
          confirmLabel={
            validationWarningAction === "run-in-explorer"
              ? t("editor.validationWarning.runAnyway", { defaultValue: "Run Anyway" })
              : t("editor.validationWarning.saveAnyway", { defaultValue: "Save Anyway" })
          }
          onConfirm={handleConfirmValidationWarning}
          onOpenChange={setShowValidationWarning}
        />
      )}
      {runnableWorkflowId && (
        <WorkflowRunsDialog
          open={showRunsDialog}
          workflowId={runnableWorkflowId}
          workflowTitle={workflowTitle}
          onOpenChange={setShowRunsDialog}
        />
      )}
    </SidebarInset>
  )
}
